import { apiClient } from './client';
import { createCrudApi } from './crud';
import type { ApiSuccess } from '../types';

export type AmcStatus = 'draft' | 'pending_approval' | 'active' | 'expiring' | 'expired' | 'renewed' | 'cancelled' | 'rejected';

export interface PmSchedule {
  id: number;
  sequenceNo: number;
  scheduledDate: string;
  dueWindowDays: number;
  status: string;
  ticketId: number | null;
}

export interface AmcContract {
  id: number;
  amcCode: string;
  clientId: number | null;
  installationId: number | null;
  productId: number | null;
  dealerId: number | null;
  serialNumber: string | null;
  oem: string | null;
  cpo: string | null;
  warrantyStartDate: string | null;
  warrantyEndDate: string | null;
  amcStartDate: string | null;
  amcEndDate: string | null;
  contractValue: number;
  contractType: string;
  pmFrequency: string;
  pmIntervalDays: number | null;
  status: AmcStatus;
  effectiveStatus: AmcStatus;
  daysToExpiry: number | null;
  rejectionReason: string | null;
  renewedFromId: number | null;
  renewedToId: number | null;
  remarks: string | null;
  createdAt: string;
  schedules?: PmSchedule[];
}

export interface CreateContractInput {
  clientId?: number | null;
  installationId?: number | null;
  productId?: number | null;
  dealerId?: number | null;
  serialNumber?: string;
  oem?: string;
  cpo?: string;
  warrantyStartDate?: string;
  warrantyEndDate?: string;
  amcStartDate: string;
  amcEndDate: string;
  contractValue?: number;
  contractType?: string;
  pmFrequency?: string;
  pmIntervalDays?: number | null;
  regionId?: number | null;
  remarks?: string;
}

export interface AmcDashboard {
  totalContracts: number;
  active: number;
  expiring: number;
  expired: number;
  byStatus: Record<string, number>;
  pmDue: number;
  pmTicketed: number;
  pmCompleted: number;
  pmMissed: number;
  slaBreaches: number;
  renewalPipeline: number;
  recent: AmcContract[];
}

const base = createCrudApi<AmcContract, CreateContractInput>('evcpd-amc/contracts');
const post = async (id: number, path: string, body: Record<string, unknown> = {}): Promise<AmcContract> =>
  (await apiClient.post<ApiSuccess<AmcContract>>(`/evcpd-amc/contracts/${id}/${path}`, body)).data.data;

export const evcpdAmcApi = {
  ...base,
  async dashboard(): Promise<AmcDashboard> {
    return (await apiClient.get<ApiSuccess<AmcDashboard>>('/evcpd-amc/contracts/dashboard')).data.data;
  },
  submit: (id: number) => post(id, 'submit'),
  approve: (id: number) => post(id, 'approve'),
  reject: (id: number, reason: string) => post(id, 'reject', { reason }),
  renew: (id: number, body: { amcStartDate: string; amcEndDate: string; contractValue?: number }) => post(id, 'renew', body),
  async generatePm(windowDays = 7): Promise<{ created: number; ticketIds: number[] }> {
    return (await apiClient.post<ApiSuccess<{ created: number; ticketIds: number[] }>>('/evcpd-amc/pm/generate', { windowDays })).data.data;
  },
  async escalate(): Promise<{ missed: number }> {
    return (await apiClient.post<ApiSuccess<{ missed: number }>>('/evcpd-amc/pm/escalate', {})).data.data;
  },
  async runRenewals(): Promise<{ sent: number; byThreshold: Record<string, number> }> {
    return (await apiClient.post<ApiSuccess<{ sent: number; byThreshold: Record<string, number> }>>('/evcpd-amc/pm/renewals/run', {})).data.data;
  },
};

// ---- Checklist templates ----
export interface ChecklistTemplateItem { id?: number; title: string; sequence?: number; expected?: string | null; isMandatory?: boolean }
export interface ChecklistTemplate {
  id: number;
  code: string;
  name: string;
  appliesTo: string;
  status: string;
  createdAt: string;
  items?: Array<{ id: number; title: string; sequence: number; expected: string | null; isMandatory: boolean }>;
}
export interface CreateChecklistInput { code: string; name: string; appliesTo?: string; status?: string; items?: ChecklistTemplateItem[] }

const checklistBase = createCrudApi<ChecklistTemplate, CreateChecklistInput>('evcpd-amc/checklists');
export const checklistTemplatesApi = { ...checklistBase };

// ---- PM execution ----
export interface PmChecklistResponse { id: number; title: string; sequence: number; mandatory: boolean; status: string; remarks: string | null }
export interface PmExecution {
  ticket: { id: number; ticketCode: string; status: string; customerConfirmed: boolean; rootCause: string | null; resolutionRemarks: string | null; photos?: Array<{ id: number; kind: string; fileName: string; fileUrl: string }> };
  checklist: PmChecklistResponse[];
  schedule: { id: number; sequenceNo: number; scheduledDate: string; status: string } | null;
  contract: { id: number; amcCode: string; dealerId: number | null } | null;
}

const pmPost = async (ticketId: number, path: string, body: Record<string, unknown> = {}): Promise<PmExecution> =>
  (await apiClient.post<ApiSuccess<PmExecution>>(`/evcpd-amc/pm/tickets/${ticketId}/${path}`, body)).data.data;

// ---- Analytics & reports ----
export interface AmcAnalytics {
  kpis: {
    totalContracts: number; active: number; expiring: number; expired: number; renewed: number;
    pmDue: number; pmCompleted: number; pmPending: number; slaBreaches: number; renewalPipeline: number; amcRevenue: number;
  };
  charts: {
    stateWiseRevenue: Array<{ label: string; value: number }>;
    pmCompliancePct: number;
    contractStatusDistribution: Array<{ label: string; value: number }>;
    oemPerformance: Array<{ label: string; value: number; revenue: number }>;
    technicianPerformance: Array<{ label: string; value: number }>;
    revenueTrend: Array<{ label: string; value: number }>;
    renewalTrend: Array<{ label: string; value: number }>;
    slaTrend: Array<{ label: string; value: number }>;
  };
}
export interface ReportMeta { type: string; title: string; columns: Array<{ key: string; header: string }> }
export interface ReportResult { type: string; title: string; columns: Array<{ key: string; header: string }>; rows: Array<Record<string, unknown>>; generatedAt: string }

export const amcAnalyticsApi = {
  async dashboard(): Promise<AmcAnalytics> {
    return (await apiClient.get<ApiSuccess<AmcAnalytics>>('/evcpd-amc/analytics')).data.data;
  },
  async reportTypes(): Promise<ReportMeta[]> {
    return (await apiClient.get<ApiSuccess<ReportMeta[]>>('/evcpd-amc/reports')).data.data;
  },
  async report(type: string, params: { from?: string; to?: string; columns?: string[] } = {}): Promise<ReportResult> {
    const qp = new URLSearchParams({ format: 'json' });
    if (params.from) qp.set('from', params.from);
    if (params.to) qp.set('to', params.to);
    if (params.columns?.length) qp.set('columns', params.columns.join(','));
    return (await apiClient.get<ApiSuccess<ReportResult>>(`/evcpd-amc/reports/${type}?${qp}`)).data.data;
  },
  /** Triggers a browser download of an xlsx/pdf export. */
  async download(path: string, format: 'xlsx' | 'pdf', params: { from?: string; to?: string; columns?: string[] } = {}): Promise<void> {
    const qp = new URLSearchParams({ format });
    if (params.from) qp.set('from', params.from);
    if (params.to) qp.set('to', params.to);
    if (params.columns?.length) qp.set('columns', params.columns.join(','));
    const res = await apiClient.get(`${path}?${qp}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${path.split('/').pop()}.${format === 'xlsx' ? 'xlsx' : 'pdf'}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  },
};

export const pmExecutionApi = {
  async get(ticketId: number): Promise<PmExecution> {
    return (await apiClient.get<ApiSuccess<PmExecution>>(`/evcpd-amc/pm/tickets/${ticketId}`)).data.data;
  },
  start: (ticketId: number) => pmPost(ticketId, 'start'),
  respond: (ticketId: number, responseId: number, body: { status: string; remarks?: string }) => pmPost(ticketId, `checklist/${responseId}`, body),
  photo: (ticketId: number, body: { kind: string; fileName: string; fileUrl: string }) => pmPost(ticketId, 'photos', body),
  observations: (ticketId: number, text: string) => pmPost(ticketId, 'observations', { text }),
  recommendation: (ticketId: number, text: string) => pmPost(ticketId, 'recommendation', { text }),
  signature: (ticketId: number, body: { customerName: string; signatureUrl?: string }) => pmPost(ticketId, 'signature', body),
  complete: (ticketId: number) => pmPost(ticketId, 'complete'),
};
